import { useCallback } from "react";
import {
  BidStatus,
  SourcingEventStatus,
  type BuyerBidDetailDto,
  useGetSourcingEventQuery,
  useRevokeBidRejectionMutation,
} from "../../../autogen/bff-api";

export type RevokeOutcome = { ok: true } | { ok: false; error: unknown };

export interface BidRejectionDecision {
  isRejected: boolean;
  canRevoke: boolean;
  lockedByAwardDecision: boolean;
  lockedByAward: boolean;
  revokeOutcome: (reason: string) => Promise<RevokeOutcome>;
  isLoading: boolean;
}

export const useBidRejectionDecision = (detail: BuyerBidDetailDto): BidRejectionDecision => {
  const [revokeRejection, { isLoading }] = useRevokeBidRejectionMutation();
  const { data: event, refetch: refetchEvent } = useGetSourcingEventQuery({ eventId: detail.bid.forEvent });

  const eventId = detail.bid.forEvent;
  const bidId = detail.bid.id;
  const isRejected = detail.bid.status === BidStatus.Rejected;

  const revokeOutcome = useCallback(
    async (reason: string): Promise<RevokeOutcome> => {
      try {
        await revokeRejection({ eventId, bidId, revokeBidRejectionRequest: { reason } }).unwrap();
        return { ok: true };
      } catch (error) {
        refetchEvent();
        return { ok: false, error };
      }
    },
    [revokeRejection, eventId, bidId, refetchEvent],
  );

  return {
    isRejected,
    canRevoke: isRejected && !!event?.status && REVOCABLE_STATUSES.includes(event.status),
    lockedByAwardDecision: isRejected && event?.status === SourcingEventStatus.AwardDecision,
    lockedByAward: isRejected && event?.status === SourcingEventStatus.Awarded,
    revokeOutcome,
    isLoading,
  };
};

const REVOCABLE_STATUSES: SourcingEventStatus[] = [
  SourcingEventStatus.Published,
  SourcingEventStatus.Evaluation,
  SourcingEventStatus.Evaluated,
];
